import { CarExpenses, CarType } from ".";
import { validateAmounts } from "../utils";
import {
  EuerDeclarationCarUsageSubformDriversLogInputs,
  EuerDeclarationCarUsageSubformCalculationResults,
} from "./types";
import {
  CAR_TYPE_COST_VALUE,
  getMinDeductibleTravelExpenses,
  VAT_RATE,
} from "./utils";

const validateDriversLogAmounts = (
  inputs: EuerDeclarationCarUsageSubformDriversLogInputs
): void => {
  // Ignoring the following rule as we are destructing the object to omit the unused values.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { carExpenses, carExpensesWithTaxDeduction, carType, ...numberValues } =
    inputs;
  const amounts: number[] = Object.values(numberValues);
  const carExpensesAmounts: number[] = Object.values(carExpenses);
  const carExpensesWithTaxDeductionAmounts: number[] = Object.values(
    carExpensesWithTaxDeduction
  );
  validateAmounts([
    ...amounts,
    ...carExpensesAmounts,
    ...carExpensesWithTaxDeductionAmounts,
  ]);
};

const sumCarExpenses = (expenses: CarExpenses): number =>
  Object.values(expenses).reduce(
    (sum: number, value: number) => sum + value,
    0
  );

/**
 * Returns the [106] Private car usage which is based on the car expenses to calculate
 * the price per km (sum of all net car expenses (19% & 0%) / total km traveled).
 * Some car expenses (depreciation & leasing costs) are recalculated based on the car type
 * before the price per km is obtained.
 * Result will be given by the privateKm * pricePerKm.
 */
const getPrivateCarUsage = ({
  carExpenses,
  carExpensesWithTaxDeduction,
  totalKm,
  privateKm,
  carType,
}: {
  carExpenses: CarExpenses;
  carExpensesWithTaxDeduction: CarExpenses;
  totalKm: number;
  privateKm: number;
  carType: CarType;
}) => {
  const carTypeCostValue = CAR_TYPE_COST_VALUE[carType];
  const carTypeBasedCarExpenses: CarExpenses = {
    ...carExpenses,
    depreciationCosts: carExpenses.depreciationCosts * carTypeCostValue,
    leasingCosts: carExpenses.leasingCosts * carTypeCostValue,
  };
  const carTypeBasedCarExpensessWithTaxDeduction: CarExpenses = {
    ...carExpensesWithTaxDeduction,
    depreciationCosts:
      carExpensesWithTaxDeduction.depreciationCosts * carTypeCostValue,
    leasingCosts: carExpensesWithTaxDeduction.leasingCosts * carTypeCostValue,
  };

  // Price per Km = sum of all net car expenses (19% & 0%) / total km traveled.
  const pricePerKm =
    totalKm > 0
      ? (sumCarExpenses(carTypeBasedCarExpenses) +
          sumCarExpenses(carTypeBasedCarExpensessWithTaxDeduction)) /
        totalKm
      : 0;

  return Math.round(privateKm * pricePerKm);
};

export const calculateCarUsageSubformDriversLogResults = (
  inputs: EuerDeclarationCarUsageSubformDriversLogInputs,
  declarationYear: number
): EuerDeclarationCarUsageSubformCalculationResults => {
  if (declarationYear <= 0) {
    throw new Error("Invalid declaration year");
  }

  validateDriversLogAmounts(inputs);

  const {
    carType,
    commuteDistance,
    commuteDays,
    totalKm,
    privateKm,
    homeCompanyKm,
    carExpenses,
    carExpensesWithTaxDeduction,
  } = inputs;

  // Price per Km = sum of all net car costs (19% & 0%) / total km traveled.
  const pricePerKm =
    totalKm > 0
      ? (sumCarExpenses(carExpenses) +
          sumCarExpenses(carExpensesWithTaxDeduction)) /
        totalKm
      : 0;

  // [106] Private Kfz-Nutzung
  const privateCarUsage = getPrivateCarUsage({
    carExpenses,
    carExpensesWithTaxDeduction,
    totalKm,
    privateKm,
    carType,
  });
  // [140] Vereinnahmte USt 19 %
  const collectedVat =
    totalKm > 0
      ? Math.round(
          sumCarExpenses(carExpensesWithTaxDeduction) *
            (privateKm / totalKm) *
            VAT_RATE
        )
      : 0;
  // [142] Fahrten zwischen Wohnung/Betrieb
  const homeCompanyTrips = Math.round(homeCompanyKm * pricePerKm);
  // [176] Min. abziehbare Fahrtkosten
  const minDeductibleTravelExpenses = getMinDeductibleTravelExpenses({
    commuteDays,
    commuteDistance,
    declarationYear,
  });

  // Gewinnerhöhung = [106] + [140] + [142] - [176]
  const profitIncrease =
    privateCarUsage +
    collectedVat +
    homeCompanyTrips -
    minDeductibleTravelExpenses;

  return {
    privateCarUsage,
    collectedVat,
    homeCompanyTrips,
    minDeductibleTravelExpenses,
    profitIncrease,
  };
};
